import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModal, NgbModalRef, NgbTypeaheadSelectItemEvent} from '@ng-bootstrap/ng-bootstrap';
import * as dayjs from 'dayjs';
import {forkJoin, Observable, Subject, Subscription, throwError} from 'rxjs';
import {debounceTime, distinctUntilChanged, finalize, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {busHolidayTypeList} from 'src/app/modules/booking-flow/holiday/holiday-search-page/helpers/holiday-types';
import {Occupancy} from 'src/app/modules/shared/interfaces/occupancy';
import sanitizeForUrl from 'src/app/utils/sanitizeForUrl';
import {BookingService} from '../../../shared/services/booking.service';
import {HolidayPrefetchService} from '../../../shared/services/holiday-prefetch.service';
import {MappingService} from '../../../shared/services/mapping.service';
import {MobileService} from '../../../shared/services/mobile.service';
import {ModalStatusService} from '../../../shared/services/modal-status.service';
import {PostMessageService} from '../../../shared/services/post-message.service';
import {ProjectService} from '../../../shared/services/project.service';
import {WidgetService} from '../../../shared/services/widget.service';
import {IDestinationBoxElement} from '../../interfaces/idestination-box-element';
import {IEngineParamChange} from '../../interfaces/iengine-param-change';
import {HolidayEngineService} from '../../services/holiday-engine.service';
import {EngineHolidayModalDatepickerComponent} from '../engine-holiday-modal-datepicker/engine-holiday-modal-datepicker.component';
import {EngineTabTypes} from './../../models/engine-tab-types.enum';

@Component({
  selector: 'app-engine-bus',
  templateUrl: './engine-bus.component.html',
  styleUrls: ['./engine-bus.component.scss']
})
export class EngineBusComponent implements OnInit, OnDestroy {
  private widgetSubscription;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  public isB2B$: Observable<boolean>;
  public isMobile$: Observable<boolean>;
  public prefetch$: Observable<any>;
  public prefetchDestinations$: Observable<any>;
  public destinationBox$: Observable<any>;

  public typeaheadLoading = false;

  public selectedOccupancy: IEngineParamChange<Occupancy>;

  public dates: any;
  public allDates: any;
  public selectedDate: any;
  public departurePlaces: any;
  public selectedDestination: IDestinationBoxElement;
  public onlyNetQuotes: boolean;
  public loadingStatus = true;
  public loadingMessage = 'Stiamo preparando le destinazioni...';
  dpModalRefSubscription: Subscription;

  private dpModalRef: NgbModalRef;

  constructor(
    private _widgetService: WidgetService,
    private _postMessageService: PostMessageService,
    private _mobileService: MobileService,
    private _ngbModal: NgbModal,
    private _modalStatusService: ModalStatusService,
    private _mappingService: MappingService,
    private _holidayPrefetchService: HolidayPrefetchService,
    private _projectService: ProjectService,
    private _holidayEngineService: HolidayEngineService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _modalService: NgbModal,
    private _bookingService: BookingService
  ) {}

  ngOnInit(): void {
    this.isB2B$ = this._postMessageService.isB2B();
    this.isMobile$ = this._mobileService.isMobile();
    this.selectedOccupancy = {value: this._bookingService.occupancy, resetParams: false};

    this._projectService.hasOnlyNetQuotes().toPromise().then(
      (onlyNetQuotes) => {
        this.onlyNetQuotes = onlyNetQuotes;
        this.prefetchDestinations$ = this._holidayPrefetchService.prefetchDestinations({
          onlyCustomHoliday: false,
          onlyNetQuotes,
          holidayTypeList: busHolidayTypeList,
          ages: this._bookingService.getAgesArray()
        });

        this.prefetchDestinations$.pipe(
          takeUntil(this.destroy$)
        ).subscribe((e) => {
          // this._holidayPrefetchService.prefetchDeparturePlaces({
          //   onlyCustomHoliday: false,
          //   onlyNetQuotes,
          //   destination: e,
          //   ages: this._bookingService.getAgesArray()
          // }).toPromise().then(
          //   (resolved) => {
          this.destinationBox$ = this._holidayEngineService.getFormattedDestionationBox(e).pipe(
            finalize(() => setTimeout(() => this.loadingStatus = false, 0))
          );
          // },
          // (rejected) => {
          //   throwError(rejected);
          // }
          // );
        });
      }
    );

    this._route.paramMap.pipe(
      takeUntil(this.destroy$)
    ).subscribe(route => {
      if (route) {
        
        this.selectedDate = route.get('departureDate') ? 
          dayjs(route.get('departureDate'), 'DD-MM-YYYY').format('MM-DD-YYYY') : 
          false;

        this.selectedDestination = {
          id: route.get('destinationId') ? Number(route.get('destinationId')) : null,
          name: route.get('destinationName'),
          value: {id: route.get('destinationId') ? Number(route.get('destinationId')) : null}
        };

        this.doPrefetch();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  onInputSelection(): void {

    this.widgetSubscription = this._widgetService.isWidget().subscribe(e => {
      if (!e) {
        document.querySelector('#searchEngine').scrollIntoView();
      }
    });

    this.widgetSubscription.unsubscribe();
  }

  selectDate(date: any): void {
    this.selectedDate = dayjs(`${date.value.day.toString().padStart(2, '0')}-${date.value.month.toString().padStart(2, '0')}-${date.value.year.toString().padStart(4, '0')}`, 'DD-MM-YYYY').format('MM-DD-YYYY');
  }

  openModal(content: any): void {
    if (!this._ngbModal.hasOpenModals()) {
      this.onInputSelection();
      this._modalStatusService.hasOpenModals.next(true);
      this._ngbModal.open(content, {
        ariaLabelledBy: 'modal-engine', size: 'xl'
      }).result.finally(() => this._modalStatusService.hasOpenModals.next(false));
    }
  }

  selectDestination(destination: IDestinationBoxElement): void {
    this.selectedDestination = destination;
    this.selectedDestination.value = this.selectedDestination;
    this.selectedDate = null;
    this._ngbModal.dismissAll();
    this.doPrefetch();
  }

  onSelectedDestination(destination: IDestinationBoxElement): void {
    this.selectDestination(destination);
  }

  destinationChangeCB($event: NgbTypeaheadSelectItemEvent): void {
    this.selectDestination($event.item);
  }

  destinationClearCB(): void {
    this.selectedDestination = null;
    this.selectedDate = null;
  }

  destinationSearch(text$: Observable<string>): Observable<any> {
    return text$.pipe(
      tap(() => this.typeaheadLoading = true),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(text => forkJoin([
        this.prefetchDestinations$,
        this._mappingService.getHolidayMappings()
      ]).pipe(
        map((e: any) => {
          return e[0]
            .map(i => e[1]?.destinations?.objects[i])
            .filter(x => x ? x.Name.toLowerCase().includes(text.toLowerCase()) : false)
            .map(i => ({name: i.Name, id: i.Id}));
        }),
        tap(() => this.typeaheadLoading = false)
      )));
  }

  destinationFormatter(result: IDestinationBoxElement): string {
    return result.name;
  }

  doPrefetch(): void {
    if (this.selectedDestination?.id) {
      this._holidayPrefetchService.prefetchDeparturePlaces({
        destination: this.selectedDestination?.id ? [this.selectedDestination?.id] : null,
        onlyCustomHoliday: false,
        holidayTypeList: busHolidayTypeList,
        onlyNetQuotes: this.onlyNetQuotes,
        ages: this._bookingService.getAgesArray()
      }).toPromise().then(
        (departurePlace) => {
          if (departurePlace?.length > 0) {
            // this._holidayPrefetchService.prefetchDate({
            //   destination: this.selectedDestination?.id ? [this.selectedDestination?.id] : null,
            //   departurePlace,
            //   onlyCustomHoliday: false,
            //   holidayTypeList: busHolidayTypeList,
            //   onlyNetQuotes: this.onlyNetQuotes,
            //   ages: this._bookingService.getAgesArray()
            // }).toPromise().then(
            //   (resolved) => {
            //     this.dates = [];
            //     this._holidayPrefetchService.setPrefetchDates(fixArray(resolved));
            //     fixArray(resolved).forEach(date => {
            //       const tmpDate = {
            //         dateLabel: dayjs(date).startOf('month').format('MMMM YYYY'),
            //         dateValue: dayjs(date).startOf('month').format('DD-MM-YYYY')
            //       };
            //       this.dates.findIndex(datePushed => datePushed.dateValue === tmpDate.dateValue) === -1 ? this.dates.push(tmpDate) : null;
            //     });
            //   },
            //   throwError
            // );
            this.departurePlaces = departurePlace;
          }
        },
        throwError
      );
    }
  }

  open(): void {

    if (!this._modalService.hasOpenModals()) {
      this._modalStatusService.hasOpenModals.next(true);

      this.dpModalRef = this._modalService.open(EngineHolidayModalDatepickerComponent, {
        ariaLabelledBy: 'modal-engine', size: 'md'
      });

      this.dpModalRef.componentInstance.selectedDestination = this.selectedDestination;
      this.dpModalRef.componentInstance.engineTabType = EngineTabTypes.Bus;
      this.dpModalRef.componentInstance.onlyCustomHoliday = false;
      this.dpModalRef.componentInstance.onlyNetQuotes = this.onlyNetQuotes;
      // this.dpModalRef.componentInstance.defaultDate = this.defaultDate;
      this.dpModalRef.componentInstance.selectedAirport = this.departurePlaces;
      this.onInputSelection();

      setTimeout(() => {
        this.dpModalRefSubscription = this.dpModalRef.componentInstance.selectedDate.subscribe(e => {
          this.selectDate(e);
        });
      });

      this.dpModalRef.result.finally(() => this._modalStatusService.hasOpenModals.next(false));
    }
  }

  searchBuses(): void {
    const occupancyString = this._bookingService.getAgesString();
    const route = `/bus/ricerca/${this.selectedDestination.id}/destinazione/${sanitizeForUrl(this.selectedDestination.name)}/il/${dayjs(this.selectedDate).format('DD-MM-YYYY')}/eta/${occupancyString}`;
    if (this._router.url === route) {
      this._holidayEngineService.setData('search', true);
    } else {
      this._router.navigate([route]);
    }
  }

}
